import React from "react"
import { categoryPageQuery } from "../../contents/categories/queries"
import SerpContainerWrapper from "../../containers/serp"
import { wrapPage } from "../../aggregators/page"
import PageNotFoundContainer from "../../containers/errors/PageNotFoundContainer"
import GenericSEO from "../../features/seo/components/GenericSEO"
import { serpDefaultQuery, serpPageQuery } from "../../contents/serp/queries"
import { sanitizeNextObject } from "../../utils/serialization"
import { DefaultLayout } from "../../layouts/Default/DefaultLayout"
import {
  fetchCategoryDocument,
  fetchCategoryPageData,
} from "../../contents/categories/fetch"
import { fetchSerpData } from "../../contents/serp/fetch"
import {
  extractCategoryPageParams,
  extractSerpPageParams,
  SerpParams,
} from "../../contents/serp/mapper"
import { WrapPage } from "../../root"
import AlgoliaSerpContainerWrapper from "../../containers/search"
import { RootData } from "../../aggregators/rootData/context"
import { fetchSerpContextData } from "../../aggregators/serp/fetch"
import { SerpContextData } from "../../aggregators/serp/types"
import { useProductSearchType } from "../../features/search/hooks/searchType"
import { TwRichTextField } from "../../root/punks/core/fields/RichTextField"
import { fetchSsrSearchData } from "../../containers/search/components/controller/state/ssr"
import { AlgoliaStaticSearchResult } from "../../containers/search/components/controller/state/types"
import AlgoliaStaticSearch from "../../containers/search/components/staticSearch"

interface Props {
  path: string
  params: SerpParams
  languageId: string
  contents: any
  category: any
  rootData: RootData
  serpData: SerpContextData
  staticSearch?: AlgoliaStaticSearchResult
}

export default function SearchPage({
  path,
  params,
  contents,
  category,
  languageId,
  rootData,
  serpData,
  staticSearch,
}: Props) {
  const searchType = useProductSearchType(rootData)
  const categoryProps = {
    hideHero: contents?.data?.info?.hideBreadcrumb ?? false,
    description: category?.data?.contents?.description ? (
      <TwRichTextField
        variant="subtitle2"
        component="p"
        value={category.data.contents.description}
      />
    ) : undefined,
    descriptionExpandable:
      category?.data?.contents?.descriptionExpandable ?? false,
  }

  return (
    <WrapPage rootData={rootData}>
      <DefaultLayout>
        {params ? (
          <>
            {searchType === "algolia" ? (
              <AlgoliaSerpContainerWrapper
                {...params}
                {...categoryProps}
                path={path}
                languageId={languageId}
                contents={contents}
                serpData={serpData}
              />
            ) : (
              <SerpContainerWrapper
                {...params}
                {...categoryProps}
                languageId={languageId}
                contents={contents}
              />
            )}
            {staticSearch && (
              <noscript>
                <AlgoliaStaticSearch staticSearch={staticSearch} />
              </noscript>
            )}
            <GenericSEO
              title={
                category?.data?.contents?.metaTitle ??
                category?.data?.name ??
                contents?.data?.seo?.title ??
                ""
              }
              description={
                category?.data?.contents?.metaDescription ??
                contents?.data?.seo?.description ??
                ""
              }
            />
          </>
        ) : (
          <>
            <PageNotFoundContainer />
            <GenericSEO
              title="Pagina non trovata"
              description="Pagina non trovata"
              noIndex
            />
          </>
        )}
      </DefaultLayout>
    </WrapPage>
  )
}

const fetchSerpPage = async (languageId: string, slug?: string) =>
  slug
    ? fetchSerpData({
        languageId,
        query: serpPageQuery,
        slug,
      })
    : fetchSerpData({
        languageId,
        query: serpDefaultQuery,
      })

const fetchSerpPageData = async (languageId: string, params: any) => {
  const category = await fetchCategoryDocument(languageId, params.path)
  const serp = await fetchSerpPage(languageId, params.path?.[0])
  if (serp?.data) {
    return {
      params: sanitizeNextObject(extractSerpPageParams(serp)),
      contents: serp,
      category,
    }
  }

  const contents = await fetchCategoryPageData({
    query: categoryPageQuery,
    languageId,
  })

  return {
    params: category
      ? sanitizeNextObject(extractCategoryPageParams(category))
      : null,
    contents,
    category,
  }
}

export async function getServerSideProps({ params, locale, query }: any) {
  return await wrapPage({
    locale,
    fetchProps: async (languageId: string, rootData: RootData) => {
      const pageData = await fetchSerpPageData(languageId, params)
      const serpContextData = await fetchSerpContextData(languageId)
      const staticSearch = pageData.params
        ? await fetchSsrSearchData({
            params: pageData.params,
            rootData,
            serpContextData,
            query,
          })
        : undefined
      return {
        ...pageData,
        serpData: sanitizeNextObject(serpContextData),
        path: params.path,
        staticSearch: sanitizeNextObject(staticSearch),
      }
    },
  })
}
